import React, { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGameModeSettingsContext } from '@/app/providers/GameModeSettingsProvider';
import { DailyModeSettings } from '@/lib/types/common/GameModeAdvancedSettings';
import { GameModeStatus } from '@/lib/types/common/GameMode';
import TextFFXIV from '@/app/components/TextFFXIV';
import ButtonFFXIV from '@/app/components/ButtonFFXIV';
import {
    isTodayDailyCompleted,
    getTodayDailyScore,
    getTodayDailyScoreData,
    formatTimeUntilNextDaily,
    getNextDailyLocalTime,
    getDailyNumber,
    DailyScore
} from '@/lib/utils/dailyUtils';
import { FaCheck } from 'react-icons/fa';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';

const DailyModeAdvancedSettings: React.FC = () => {
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const { updateSettings, isGameMaster } = useGameModeSettingsContext();
    const { showPopup } = useGlobalPopup();
    const { play } = useAudio();
    const [isCompleted, setIsCompleted] = useState(false);
    const [score, setScore] = useState<number | null>(null);
    const [dailyScoreData, setDailyScoreData] = useState<DailyScore | null>(null);
    const [timeUntilNext, setTimeUntilNext] = useState('');
    const [nextDailyTime, setNextDailyTime] = useState('');

    // Check status on mount and set up timer
    useEffect(() => {
        // Initial check
        const completed = isTodayDailyCompleted();
        const dailyScore = getTodayDailyScore();
        const dailyData = getTodayDailyScoreData();
        setIsCompleted(completed);
        setScore(dailyScore);
        setDailyScoreData(dailyData);
        setTimeUntilNext(formatTimeUntilNextDaily());
        setNextDailyTime(getNextDailyLocalTime());

        // Update countdown every second
        const interval = setInterval(() => {
            setTimeUntilNext(formatTimeUntilNextDaily());
        }, 1000);

        return () => clearInterval(interval);
    }, []); // Empty dependency array - only run once on mount

    // Apply daily mode settings when component mounts
    useEffect(() => {
        if (isGameMaster) {
            const dailySettings: Partial<DailyModeSettings> = {
                maxRounds: 5,
                timerSeconds: 60,
                hasSpecialRules: true,
                status: GameModeStatus.AVAILABLE,
                minPlayers: 1,
                maxPlayers: 1,
                dailySeed: ''
            };
            updateSettings(dailySettings);
        }
    }, [isGameMaster]);

    const handleCopyDailyRecap = () => {
        if (!dailyScoreData) return;

        const dailyNumber = getDailyNumber();
        
        // Build the recap text
        let recapText = t('play.dailyRecapHeader', { score: dailyScoreData.score, dailyNumber }) + '\n';
        
        // Add each round score
        dailyScoreData.roundScores.forEach((roundScore, index) => {
            const roundNum = index + 1;
            recapText += t('play.dailyRecapRound', { roundNum, roundScore }) + '\n';
        });
        
        recapText += `https://eorguessr.com`;

        // Copy to clipboard
        navigator.clipboard.writeText(recapText).then(() => {
            play(AUDIO.CLICK);
            showPopup({
                type: 'success',
                message: t('play.copyRecapSuccess') || 'Recap copied to clipboard!',
            });
        }).catch((err) => {
            console.error('Failed to copy recap:', err);
            showPopup({
                type: 'error',
                message: t('play.copyRecapError') || 'Failed to copy recap',
            });
        });
    };

    if (!room || !gameState) return null;

    return (
        <div className="flex flex-col gap-7">
            {/* Daily Mode Info */}
            <div className="flex flex-col gap-2 text-shadow-border shadow-black">
                <TextFFXIV
                    text={t('game.modes.daily.settingsInfo')}
                    className="text-shadow-border shadow-black text-gray-300"
                />
            </div>

            {isCompleted && score !== null ? (
                <>
                    {/* Completion Status */}
                    <div className="flex flex-col gap-2 text-shadow-border shadow-black">
                        <div className="flex items-center gap-2 text-green-400">
                            <div className="text-orange-500">{t('game.modes.daily.completed')}</div>
                            <FaCheck className="text-lg" />
                        </div>
                        <div className="text-2xl text-orange-500">
                            {`${t('game.modes.daily.yourScore')}: ${score}`}
                        </div>
                        
                        {/* Round Scores */}
                        {dailyScoreData && dailyScoreData.roundScores && dailyScoreData.roundScores.length > 0 && (
                            <div className="flex flex-col gap-1">
                                {dailyScoreData.roundScores.map((roundScore, index) => (
                                    <div key={index} className="text-sm text-orange-500">
                                        {t('play.dailyRecapRound', { roundNum: index + 1, roundScore })}
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Copy Recap Button */}
                        {dailyScoreData && (
                            <div className="mt-3">
                                <ButtonFFXIV
                                    onClick={handleCopyDailyRecap}
                                    text={t('play.copyRecap')}
                                    fitContent={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Next Daily Timer */}
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 text-white">
                            <TextFFXIV
                                text={t('game.modes.daily.nextDaily')}
                                className=""
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="text-2xl font-mono text-white">
                                {timeUntilNext}
                            </div>
                        </div>
                        <div className="text-sm text-gray-400">
                            <TextFFXIV
                                text={`${t('game.modes.daily.resetsAt')} ${nextDailyTime}`}
                                className="text-shadow-border shadow-black"
                            />
                        </div>
                    </div>
                </>

            ) : (
                <div className="flex flex-col gap-2 text-shadow-border shadow-black">
                    <div className="flex flex-col text-shadow-border shadow-black">
                        <div className="flex items-center gap-2 text-green-400">
                            <div className="text-green-400">{t('game.modes.daily.notCompleted')}</div>
                            <FaCheck className="text-lg" />
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default DailyModeAdvancedSettings;
